import React, { useEffect, useRef, useState } from 'react'
import { analysisApi } from '../../services/analysis'
import { Screen } from '../../components/lease/Screen'
import { BrandHeading } from '../../components/lease/BrandHeading'

// "Reviewing Your Lease" (mockup 10): analyses take 1-3 minutes, so poll the
// status endpoint until the background task lands on completed/failed.
const POLL_MS = 5000

const MESSAGES = [
  'Reading your lease…',
  'Checking Colorado compliance rules…',
  'Confirming findings with our AI reviewer…',
  'Scoring and assembling your Risk Report…',
]

const CATEGORIES = [
  ['Legal Terms', '5 new compliance points'],
  ['Risk Analysis', '2 new recommendations'],
  ['Legal Alerts', '9 new findings'],
  ['Legal Advice', '5 new risk points'],
]

function CheckIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" className="h-3.5 w-3.5" aria-hidden="true">
      <path d="M5 10.5l3.2 3.2L15 6.5" stroke="currentColor" strokeWidth="2.4"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export function Reviewing({ documentId, onComplete, onFailed }) {
  const [tick, setTick] = useState(0)
  const done = useRef(false)

  useEffect(() => {
    let cancelled = false
    let timer
    const poll = async () => {
      try {
        const s = await analysisApi.getStatus(documentId)
        if (cancelled || done.current) return
        if (s.analysis_status === 'completed') {
          done.current = true
          onComplete()
          return
        }
        if (s.analysis_status === 'failed') {
          done.current = true
          onFailed(s.error_message || 'Analysis failed')
          return
        }
      } catch {
        /* transient — keep polling */
      }
      setTick((t) => t + 1)
      timer = setTimeout(poll, POLL_MS)
    }
    timer = setTimeout(poll, POLL_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [documentId, onComplete, onFailed])

  return (
    <Screen className="text-center">
      <header className="animate-fade-in">
        <BrandHeading className="text-3xl">Reviewing your lease</BrandHeading>
      </header>

      <div className="mt-8 animate-slide-in h-2 w-full overflow-hidden rounded-full bg-emerald-100 dark:bg-slate-700">
        <div className="h-full w-2/3 animate-pulse rounded-full bg-gradient-to-r from-emerald-600 to-teal-600" />
      </div>

      <p className="mt-6 animate-slide-in text-slate-700 dark:text-slate-200">{MESSAGES[tick % MESSAGES.length]}</p>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        This usually takes 1–3 minutes. Keep this tab open.
      </p>

      <section className="mt-8 animate-slide-in">
        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Analysis Categories</h3>
        <div className="mt-4 grid grid-cols-2 gap-3 text-left">
          {CATEGORIES.map(([label, detail]) => (
            <div
              key={label}
              className="rounded-2xl bg-white/70 p-4 shadow-sm ring-1 ring-emerald-100 backdrop-blur dark:bg-slate-800/60 dark:ring-slate-700"
            >
              <span className="flex items-center gap-2">
                <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow">
                  <CheckIcon />
                </span>
                <span className="font-semibold text-slate-800 dark:text-slate-100">{label}</span>
              </span>
              <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">{detail}</p>
            </div>
          ))}
        </div>
      </section>
    </Screen>
  )
}

export default Reviewing
